import { useState } from 'react';
import type { ReactElement } from 'react';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import Decimal from 'decimal.js';
import { fetchBacktest, fetchBacktests, startBacktest } from '../api/endpoints';
import { errorMessageRu } from '../api/errors';
import { queryKeys } from '../api/query-keys';
import type { BacktestDetail, BacktestSummary, Trade } from '../api/schemas';
import { useSession } from '../auth/session-context';
import { withReauth } from '../auth/with-reauth';
import { Banner } from '../components/Banner';
import { EmptyState } from '../components/EmptyState';
import { Field } from '../components/Field';
import { Percent, Pnl, Price, Quantity, Usdt } from '../components/Money';
import { QueryState } from '../components/QueryState';
import { ReasonText } from '../components/ReasonText';
import { Section } from '../components/Section';
import { StatusPill } from '../components/StatusPill';
import { TableScroll } from '../components/TableScroll';
import { BACKTEST_STATUS_LABEL, BACKTEST_STATUS_TONE, SIDE_LABEL } from '../domain/labels';
import { formatDateOnly, formatDateTime, isoDateDaysAgo, todayIsoDate } from '../lib/datetime';
import { newIdempotencyKey } from '../lib/idempotency';
import { formatUsdt } from '../lib/money';

/**
 * Раздел 6 — «Бэктест».
 *
 * Launch a run, watch the queue, then read the metrics, the monthly breakdown
 * and the trade list of a finished run. Every figure arrives as a decimal
 * string; the only arithmetic done on this screen is the monthly total, and it
 * goes through decimal.js, never `+`.
 */
export function BacktestPage(): ReactElement {
  const [selectedId, setSelectedId] = useState<string | null>(null);

  const runsQuery = useQuery({
    queryKey: queryKeys.backtests,
    queryFn: ({ signal }) => fetchBacktests(signal),
  });

  return (
    <>
      <div className="page-head">
        <h1>Бэктест</h1>
        <span className="page-head__hint">
          Прогон стратегии на исторических данных тем же движком, что и в реальном времени.
        </span>
      </div>

      <div className="stack">
        <LaunchSection />

        <Section title="Прогоны" hint="Список запусков, новые сверху.">
          <QueryState query={runsQuery} what="список прогонов">
            {(data) =>
              data.items.length === 0 ? (
                <EmptyState>Прогонов пока нет.</EmptyState>
              ) : (
                <RunsTable
                  runs={data.items}
                  selectedId={selectedId}
                  onSelect={(id) => {
                    setSelectedId(id);
                  }}
                />
              )
            }
          </QueryState>
        </Section>

        {selectedId !== null && <RunDetail id={selectedId} />}
      </div>
    </>
  );
}

function LaunchSection(): ReactElement {
  const queryClient = useQueryClient();
  const { canOperate, promptReauth } = useSession();
  const [from, setFrom] = useState(() => isoDateDaysAgo(365));
  const [to, setTo] = useState(() => todayIsoDate());
  const [errorRu, setErrorRu] = useState<string | null>(null);

  const mutation = useMutation({
    mutationFn: () =>
      withReauth(() => startBacktest({ from, to }, newIdempotencyKey()), promptReauth),
    onSuccess: async () => {
      setErrorRu(null);
      await queryClient.invalidateQueries({ queryKey: queryKeys.backtests });
    },
    onError: (error: unknown) => {
      setErrorRu(errorMessageRu(error));
    },
  });

  const invalidRange = from === '' || to === '' || from > to;

  return (
    <Section title="Запуск прогона" hint="Период задаётся календарными датами включительно.">
      <div className="stack stack--tight">
        {!canOperate && (
          <Banner tone="warn" title="Только просмотр.">
            Ваша роль не позволяет запускать бэктест.
          </Banner>
        )}
        {errorRu !== null && (
          <Banner tone="danger" title="Прогон не запущен.">
            {errorRu}
          </Banner>
        )}
        {mutation.isSuccess && errorRu === null && (
          <Banner tone="ok" title="Прогон поставлен в очередь.">
            Результаты появятся в списке после завершения расчёта.
          </Banner>
        )}

        <div className="filters">
          <Field id="bt-from" label="Дата с">
            <input
              id="bt-from"
              type="date"
              value={from}
              onChange={(event) => {
                setFrom(event.target.value);
              }}
            />
          </Field>
          <Field
            id="bt-to"
            label="Дата по"
            error={invalidRange ? 'Дата начала должна быть не позже даты окончания.' : undefined}
          >
            <input
              id="bt-to"
              type="date"
              value={to}
              aria-invalid={invalidRange}
              onChange={(event) => {
                setTo(event.target.value);
              }}
            />
          </Field>
          <button
            type="button"
            className="btn btn--primary"
            disabled={!canOperate || invalidRange || mutation.isPending}
            onClick={() => {
              mutation.mutate();
            }}
          >
            {mutation.isPending ? 'Запуск…' : 'Запустить бэктест'}
          </button>
        </div>
      </div>
    </Section>
  );
}

function RunsTable({
  runs,
  selectedId,
  onSelect,
}: {
  runs: readonly BacktestSummary[];
  selectedId: string | null;
  onSelect: (id: string) => void;
}): ReactElement {
  return (
    <TableScroll label="Таблица прогонов, прокручивается по горизонтали">
      <table className="data-table data-table--narrow">
        <caption>Запущенные прогоны бэктеста</caption>
        <thead>
          <tr>
            <th scope="col">Создан</th>
            <th scope="col">Период</th>
            <th scope="col">Статус</th>
            <th scope="col">Результаты</th>
          </tr>
        </thead>
        <tbody>
          {runs.map((run) => (
            <tr key={run.id}>
              <td>{formatDateTime(run.createdAt)}</td>
              <td>
                {formatDateOnly(run.from)} — {formatDateOnly(run.to)}
              </td>
              <td>
                <StatusPill tone={BACKTEST_STATUS_TONE[run.status]} srPrefix="Статус прогона">
                  {BACKTEST_STATUS_LABEL[run.status]}
                </StatusPill>
              </td>
              <td>
                <button
                  type="button"
                  className="btn btn--ghost"
                  aria-pressed={selectedId === run.id}
                  onClick={() => {
                    onSelect(run.id);
                  }}
                >
                  Показать результаты
                </button>
              </td>
            </tr>
          ))}
        </tbody>
      </table>
    </TableScroll>
  );
}

function RunDetail({ id }: { id: string }): ReactElement {
  const query = useQuery({
    queryKey: queryKeys.backtest(id),
    queryFn: ({ signal }) => fetchBacktest(id, signal),
  });

  return (
    <QueryState query={query} what="результаты прогона">
      {(detail) => <RunDetailContent detail={detail} />}
    </QueryState>
  );
}

function RunDetailContent({ detail }: { detail: BacktestDetail }): ReactElement {
  return (
    <div className="stack">
      <Section
        title={`Результаты прогона за ${formatDateOnly(detail.from)} — ${formatDateOnly(detail.to)}`}
      >
        <div className="stack stack--tight">
          <div className="row">
            <StatusPill tone={BACKTEST_STATUS_TONE[detail.status]} srPrefix="Статус прогона">
              {BACKTEST_STATUS_LABEL[detail.status]}
            </StatusPill>
          </div>

          {detail.errorRu !== null && (
            <Banner tone="danger" title="Прогон завершился ошибкой.">
              {detail.errorRu}
            </Banner>
          )}

          {detail.metrics === null ? (
            <EmptyState>Метрики появятся после завершения расчёта.</EmptyState>
          ) : (
            <div className="metrics">
              <Metric label="Решений" value={String(detail.metrics.decisions)} />
              <Metric label="Сделок" value={String(detail.metrics.trades)} />
              <Metric label="Доля лонгов" value={<Percent value={detail.metrics.longShare} />} />
              <Metric label="Доля шортов" value={<Percent value={detail.metrics.shortShare} />} />
              <Metric label="Доля пропусков" value={<Percent value={detail.metrics.skipShare} />} />
              <Metric label="Доля прибыльных" value={<Percent value={detail.metrics.winRate} />} />
              <Metric label="PnL до комиссий" value={<Pnl value={detail.metrics.grossPnl} />} />
              <Metric label="PnL после комиссий" value={<Pnl value={detail.metrics.netPnl} />} />
              <Metric label="Комиссии" value={<Usdt value={detail.metrics.fees} />} />
              <Metric label="Funding" value={<Usdt value={detail.metrics.funding} />} />
              <Metric
                label="Максимальная просадка"
                value={<Usdt value={detail.metrics.maxDrawdown} />}
              />
              <Metric label="Profit factor" value={detail.metrics.profitFactor} />
              <Metric
                label="Средний результат сделки"
                value={<Pnl value={detail.metrics.averageTradeResult} />}
              />
              <Metric
                label="Худшая серия убытков"
                value={String(detail.metrics.worstLossStreak)}
                note="сделок подряд"
              />
              <Metric label="Импульсных пауз" value={String(detail.metrics.cooldownEvents)} />
              <Metric label="Ликвидаций" value={String(detail.metrics.liquidations)} />
            </div>
          )}
        </div>
      </Section>

      <Section title="Помесячный результат">
        {detail.monthly.length === 0 ? (
          <EmptyState>Помесячной разбивки нет.</EmptyState>
        ) : (
          <MonthlyTable monthly={detail.monthly} />
        )}
      </Section>

      <Section title="Сделки прогона">
        {detail.trades.length === 0 ? (
          <EmptyState>Сделок в прогоне нет.</EmptyState>
        ) : (
          <BacktestTradesTable trades={detail.trades} />
        )}
      </Section>
    </div>
  );
}

function Metric({
  label,
  value,
  note,
}: {
  label: string;
  value: ReactElement | string;
  note?: string;
}): ReactElement {
  return (
    <div>
      <span className="metric__label">{label}</span>
      <span className="metric__value">{value}</span>
      {note !== undefined && <span className="metric__note">{note}</span>}
    </div>
  );
}

function MonthlyTable({
  monthly,
}: {
  monthly: readonly { month: string; netPnl: string; trades: number }[];
}): ReactElement {
  // decimal.js, not `+`: summing money as floats is exactly the bug this app
  // is built to avoid.
  const total = monthly.reduce((acc, row) => acc.plus(new Decimal(row.netPnl)), new Decimal(0));
  const totalTrades = monthly.reduce((acc, row) => acc + row.trades, 0);

  return (
    <TableScroll label="Помесячная таблица, прокручивается по горизонтали">
      <table className="data-table data-table--narrow">
        <caption>Результат по месяцам</caption>
        <thead>
          <tr>
            <th scope="col">Месяц</th>
            <th scope="col" className="num">
              PnL после комиссий
            </th>
            <th scope="col" className="num">
              Сделок
            </th>
          </tr>
        </thead>
        <tbody>
          {monthly.map((row) => (
            <tr key={row.month}>
              <td>{formatDateOnly(row.month)}</td>
              <td className="num">
                <Pnl value={row.netPnl} />
              </td>
              <td className="num">{row.trades}</td>
            </tr>
          ))}
        </tbody>
        <tfoot>
          <tr>
            <th scope="row">Итого</th>
            <td className="num">{formatUsdt(total.toFixed(2))}</td>
            <td className="num">{totalTrades}</td>
          </tr>
        </tfoot>
      </table>
    </TableScroll>
  );
}

function BacktestTradesTable({ trades }: { trades: readonly Trade[] }): ReactElement {
  return (
    <TableScroll label="Таблица сделок прогона, прокручивается по горизонтали">
      <table className="data-table">
        <caption>Сделки, полученные в прогоне</caption>
        <thead>
          <tr>
            <th scope="col">Открыта</th>
            <th scope="col">Закрыта</th>
            <th scope="col">Сторона</th>
            <th scope="col" className="num">
              Вход
            </th>
            <th scope="col" className="num">
              Выход
            </th>
            <th scope="col" className="num">
              Объём
            </th>
            <th scope="col" className="num">
              PnL после комиссий
            </th>
            <th scope="col" className="wrap">
              Причина входа
            </th>
            <th scope="col" className="wrap">
              Причина выхода
            </th>
          </tr>
        </thead>
        <tbody>
          {trades.map((trade) => (
            <tr key={trade.tradeId}>
              <td>{formatDateTime(trade.openedAt)}</td>
              <td>{trade.closedAt === null ? '—' : formatDateTime(trade.closedAt)}</td>
              <td>{SIDE_LABEL[trade.side]}</td>
              <td className="num">
                <Price value={trade.entryPrice} />
              </td>
              <td className="num">
                <Price value={trade.exitPrice} />
              </td>
              <td className="num">
                <Quantity value={trade.quantity} />
              </td>
              <td className="num">
                <Pnl value={trade.netPnl} />
              </td>
              <td className="wrap">
                <ReasonText code={trade.entryReasonCode} />
              </td>
              <td className="wrap">
                {trade.exitReasonCode === null ? (
                  <span className="muted">—</span>
                ) : (
                  <ReasonText code={trade.exitReasonCode} />
                )}
              </td>
            </tr>
          ))}
        </tbody>
      </table>
    </TableScroll>
  );
}
